import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonInput,
  IonText,
  IonSpinner,
} from '@ionic/react';
import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { INCREASE_RELEASE_CLAUSE } from '../../../../graphql/mutations/releaseClause';
import { GET_TEAM_TAB_DATA } from '../../../../graphql/queries/teamTabData';
import type {
  IncreaseReleaseClauseMutation,
  IncreaseReleaseClauseMutationVariables,
  Player,
} from '../../../../types/graphql-generated';
import { PlayerCard } from '../../components/PlayerCard';

interface IncreaseReleaseClauseModalProps {
  isOpen: boolean;
  player: Player;
  currentReleaseClause: number;
  budget: number;
  leagueId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const IncreaseReleaseClauseModal = ({
  isOpen,
  player,
  currentReleaseClause,
  budget,
  leagueId,
  onClose,
  onSuccess,
}: IncreaseReleaseClauseModalProps) => {
  const [investmentAmount, setInvestmentAmount] = useState('');
  const [errors, setErrors] = useState<string[]>([]);

  const [increaseClause, { loading }] = useMutation<
    IncreaseReleaseClauseMutation,
    IncreaseReleaseClauseMutationVariables
  >(INCREASE_RELEASE_CLAUSE, {
    refetchQueries: [{ query: GET_TEAM_TAB_DATA, variables: { fantasyLeagueId: leagueId } }],
  });

  const investmentValue = parseInt(investmentAmount, 10);
  const isValidInvestment = !isNaN(investmentValue) && investmentValue > 0;
  const newClause = isValidInvestment ? currentReleaseClause + investmentValue * 2 : null;

  const handleConfirm = async () => {
    setErrors([]);

    if (!isValidInvestment) {
      setErrors(['La inversión debe ser un número mayor que 0']);
      return;
    }

    try {
      const result = await increaseClause({
        variables: {
          input: {
            playerId: player.id,
            fantasyLeagueId: leagueId,
            investmentAmount: investmentValue,
          },
        },
      });

      const mutationErrors = result.data?.increaseReleaseClause?.errors;
      if (mutationErrors && mutationErrors.length > 0) {
        setErrors(mutationErrors);
      } else {
        onSuccess();
        handleClose();
      }
    } catch {
      setErrors(['Error al aumentar la cláusula']);
    }
  };

  const handleClose = () => {
    setErrors([]);
    setInvestmentAmount('');
    onClose();
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={handleClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle className="ml-4">Aumentar cláusula</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleClose}>Cancelar</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div className="mb-6">
          <PlayerCard player={player} />
        </div>

        <div className="mb-6 space-y-3 rounded-xl bg-slate-50 px-4 py-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-slate-600">Cláusula actual</span>
            <span className="font-semibold text-slate-800">
              {currentReleaseClause.toLocaleString('es-ES')}€
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-600">Tu presupuesto</span>
            <span className="font-semibold text-slate-800">
              {budget.toLocaleString('es-ES')}€
            </span>
          </div>
          {newClause !== null && (
            <div className="flex items-center justify-between border-t border-slate-200 pt-3">
              <span className="font-medium text-slate-700">Nueva cláusula</span>
              <span className="font-bold text-[var(--ion-color-primary)]">
                {newClause.toLocaleString('es-ES')}€
              </span>
            </div>
          )}
        </div>

        <IonInput
          className="mb-2 text-sm font-semibold"
          type="number"
          label="Inversión (€)"
          labelPlacement="floating"
          fill="outline"
          value={investmentAmount}
          onIonInput={(e) => setInvestmentAmount(e.detail.value || '')}
          placeholder="Introduce el importe"
          min="1"
          step="1"
          inputmode="numeric"
          data-testid="investment-amount-input"
        />
        <p className="mb-4 text-xs text-slate-500">
          Cada euro invertido aumenta la cláusula en 2€
        </p>

        {errors.length > 0 && (
          <div className="mb-4">
            {errors.map((error, index) => (
              <IonText color="danger" key={index}>
                <p className="text-sm">{error}</p>
              </IonText>
            ))}
          </div>
        )}

        <IonButton
          expand="block"
          color="primary"
          onClick={handleConfirm}
          disabled={loading || !isValidInvestment}
          data-testid="confirm-increase-clause"
        >
          {loading ? <IonSpinner name="crescent" /> : 'Confirmar inversión'}
        </IonButton>
      </IonContent>
    </IonModal>
  );
};
